import React, { useState } from "react";
import "./FirmwareUpdate.css";
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import Accordion from 'react-bootstrap/Accordion';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faRefresh, faCode, faCloudArrowDown } from "@fortawesome/free-solid-svg-icons";
import { sleep } from "../../../lib/utils";

const DEFAULT_BRANCHES = ['master', 'alpha', 'beta', 'trunk'];

function FirmwareUpdate({ bond, target, onUpgradeComplete }) {
    const [branch, setBranch] = useState('master');
    const [confirmedBranch, setConfirmedBranch] = useState('');
    const [versions, setVersions] = useState({});
    const [selectedVersion, setSelectedVersion] = useState('');
    const [loading, setLoading] = useState(false);
    const [upgrading, setUpgrading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [status, setStatus] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    async function loadVersions() {
        if (!target || !branch) {
            setError('Please enter a branch name');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');
        setVersions({});
        setSelectedVersion('');
        setConfirmedBranch('');

        try {
            const TARGET_SUFFIXED_BRANCHES = ['master', 'alpha', 'beta'];
            const normalizedBranch = branch.replace(/\//g, '-');
            const branchPath = TARGET_SUFFIXED_BRANCHES.includes(branch)
                ? `${branch}-${target}`
                : normalizedBranch;
            const versionsUrl = `https://s3.amazonaws.com/bond-updates/v2/${target}/${branchPath}/versions_internal.json`;

            const result = await fetch(versionsUrl, {
                headers: { "Accept-Encoding": "gzip" }
            });

            if (!result.ok) {
                throw new Error(`Branch "${branch}" not found or no versions available`);
            }

            const resultJson = await result.json();
            const versionsList = resultJson['versions'];

            if (!versionsList || versionsList.length === 0) {
                throw new Error(`No versions found for branch "${branch}"`);
            }

            let versionsMap = {};
            versionsList.forEach((v) => {
                versionsMap[v['version']] = v;
            });

            setVersions(versionsMap);
            setConfirmedBranch(branch);

            // Auto-select latest (first in sorted list) version by build_date
            if (versionsList.length > 0) {
                const sortedVersions = Object.keys(versionsMap).sort((a, b) => {
                    const dateA = new Date(versionsMap[a].build_date || 0);
                    const dateB = new Date(versionsMap[b].build_date || 0);
                    return dateB - dateA; // Sort descending (newest first)
                });
                setSelectedVersion(sortedVersions[0]);
            }
        } catch (err) {
            setError(err.message);
            console.error('Error loading versions:', err);
        } finally {
            setLoading(false);
        }
    }

    async function startUpgrade() {
        if (!selectedVersion || !versions[selectedVersion]) {
            setError('Please select a valid version');
            return;
        }

        setUpgrading(true);
        setError('');
        setSuccess('');
        setProgress(0);
        setStatus('Starting upgrade...');

        try {
            const fwVersionConfig = versions[selectedVersion];

            // Start the upgrade
            const upgradeResponse = await bond.put('/sys/upgrade', fwVersionConfig);
            if (upgradeResponse && !upgradeResponse.ok) {
                const detail = upgradeResponse.status === 405
                    ? 'Write access is disabled for this proxy (405 Method Not Allowed).'
                    : `Server returned ${upgradeResponse.status}`;
                throw new Error(detail);
            }

            // Poll for progress
            while (true) {
                await sleep(3000);

                const response = await bond.get('/sys/upgrade');
                const progressValue = parseInt(response["progress"]);
                const progressPercent = Math.floor(progressValue / 10);

                setProgress(progressPercent);
                setStatus(`Upgrading: ${progressPercent}%`);

                if (progressValue === 1000) {
                    setStatus('Upgrade complete! Rebooting...');
                    await bond.put('/sys/reboot', {});
                    await sleep(2000);
                    break;
                } else if (progressValue === -1) {
                    throw new Error('Upgrade failed: ' + JSON.stringify(response));
                }
            }

            // Wait for reboot
            setStatus('Waiting for reboot...');
            await waitReboot();

            setSuccess(`Successfully upgraded to ${selectedVersion}!`);
            setStatus('');
            setProgress(0);

            // Refresh version info after successful upgrade
            if (onUpgradeComplete) {
                await onUpgradeComplete();
            }
        } catch (err) {
            setError(`Upgrade failed: ${err.message}`);
            console.error('Upgrade error:', err);
        } finally {
            setUpgrading(false);
        }
    }

    async function waitReboot() {
        let attempts = 0;
        const maxAttempts = 60; // 5 minutes max

        while (attempts < maxAttempts) {
            try {
                await sleep(5000);
                const response = await bond.get('/sys/version');

                if (response && response.fw_ver) {
                    setStatus('Device rebooted successfully!');
                    return;
                }
            } catch (err) {
                // Expected during reboot
                setStatus(`Waiting for reboot... (${attempts * 5}s)`);
            }
            attempts++;
        }

        throw new Error('Reboot timeout - device did not respond after 5 minutes');
    }

    const versionsList = Object.keys(versions).sort((a, b) => {
        const dateA = new Date(versions[a].build_date || 0);
        const dateB = new Date(versions[b].build_date || 0);
        return dateB - dateA; // Sort descending by build_date (newest first)
    });

    const handleAccordionToggle = (eventKey) => {
        const expanded = eventKey === '0';

        // Load versions when accordion is expanded for the first time
        if (expanded && !confirmedBranch && !loading && branch && target) {
            loadVersions();
        }
    };

    return (
        <Accordion className="firmware-update-accordion" onSelect={handleAccordionToggle}>
            <Accordion.Item eventKey="0">
                <Accordion.Header>
                    <FontAwesomeIcon icon={faCloudArrowDown} className="me-2" />
                    Firmware Update
                </Accordion.Header>
                <Accordion.Body>
                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError('')}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                            {success}
                        </Alert>
                    )}

            <Form.Group className="mb-3">
                <Form.Label>Branch</Form.Label>
                <div className="d-flex gap-2">
                    <Form.Control
                        type="text"
                        list="branch-suggestions"
                        value={branch}
                        onChange={(e) => setBranch(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                loadVersions();
                            }
                        }}
                        disabled={upgrading || loading}
                        placeholder="Enter or select a branch"
                        className="flex-grow-1"
                    />
                    <Button
                        variant={confirmedBranch === branch && versionsList.length > 0 ? "success" : "primary"}
                        onClick={loadVersions}
                        disabled={upgrading || loading || !branch}
                    >
                        {loading ? (
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                            />
                        ) : (
                            <FontAwesomeIcon icon={faRefresh} />
                        )}
                    </Button>
                </div>
                <datalist id="branch-suggestions">
                    {DEFAULT_BRANCHES.map(b => (
                        <option key={b} value={b} />
                    ))}
                </datalist>
            </Form.Group>

            {confirmedBranch && versionsList.length > 0 && (
                <>
                    <Form.Group className="mb-3">
                        <Form.Label>Version</Form.Label>
                        <Form.Select
                            value={selectedVersion}
                            onChange={(e) => setSelectedVersion(e.target.value)}
                            disabled={upgrading}
                        >
                            {versionsList.map(v => (
                                <option key={v} value={v}>{v}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    {selectedVersion && versions[selectedVersion] && (
                        <div className="version-info mb-3">
                            <div className="mb-2">
                                <small className="text-muted d-block">Build Date</small>
                                <strong>{versions[selectedVersion].build_date || 'N/A'}</strong>
                            </div>

                            <Accordion className="mt-2">
                                <Accordion.Item eventKey="0">
                                    <Accordion.Header>
                                        <FontAwesomeIcon icon={faCode} className="me-2" />
                                        <small>JSON Info</small>
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        <pre className="version-json">
                                            {JSON.stringify(versions[selectedVersion], null, 2)}
                                        </pre>
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>
                        </div>
                    )}

                    <div className="d-grid gap-2 mb-3">
                        <Button
                            size="lg"
                            variant="primary"
                            onClick={startUpgrade}
                            disabled={upgrading || !selectedVersion}
                        >
                            {upgrading ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        className="me-2"
                                    />
                                    Upgrading...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faDownload} size="xl" className="me-4" />
                                    Start Upgrade
                                </>
                            )}
                        </Button>
                    </div>
                </>
            )}

            {upgrading && (
                <div className="upgrade-progress">
                    <ProgressBar
                        now={progress}
                        label={`${progress}%`}
                        animated
                        striped
                        className="mb-2"
                    />
                    <div className="text-center text-muted small">
                        {status}
                    </div>
                </div>
            )}

                    {status && !upgrading && (
                        <div className="text-center text-muted small mt-2">
                            {status}
                        </div>
                    )}
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );
}

export default FirmwareUpdate;
